import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import PageLayout from "../components/PageLayout";
import { EmptyState } from "../components/EmptyState";
import { PosterImage } from "../components/PosterImage";
import { Playlists } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useCatalog } from "../contexts/CatalogContext";
import { db } from "../lib/db";
import { Resume } from "../lib/resume";
import type { Episode, SeriesDetail } from "../lib/types";

export default function SeriesDetailPage() {
  const { seriesId = "" } = useParams<{ seriesId: string }>();
  const { activePlaylist, catalog } = useCatalog();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<SeriesDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeSeason, setActiveSeason] = useState<string>("");
  // Whether any episode of this series has a saved resume. Drives the
  // "Mark series as watched" button visibility.
  const [hasSeriesResume, setHasSeriesResume] = useState(false);

  const seriesMeta = catalog?.series.streams.find((s) => s.id === seriesId);

  useEffect(() => {
    if (!activePlaylist) return;
    setDetail(null);
    setError(null);
    Playlists.seriesDetail(activePlaylist.id, seriesId)
      .then((d) => {
        setDetail(d);
        const seasons = Object.keys(d.episodes || {});
        if (seasons.length) setActiveSeason(seasons[0]);
      })
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Failed"));
  }, [activePlaylist, seriesId]);

  // Check Dexie cache for any episode-level resume row whose seriesId
  // matches. If present, surface the "Mark series as watched" button.
  // Episodes that haven't been opened yet won't have a row; that's fine —
  // the click wipes the server-side rows for the whole series anyway.
  useEffect(() => {
    if (!user || !activePlaylist || !detail) return;
    const epIds = new Set<string>();
    for (const eps of Object.values(detail.episodes || {})) {
      if (Array.isArray(eps)) {
        for (const ep of eps) epIds.add(String(ep.id));
      }
    }
    db.resume
      .where("playlistId")
      .equals(activePlaylist.id)
      .filter((r) => r.streamKind === "series" && epIds.has(r.streamId))
      .first()
      .then((row) => setHasSeriesResume(!!row))
      .catch(() => setHasSeriesResume(false));
  }, [user, activePlaylist, detail]);

  const markSeriesWatched = async () => {
    if (!user || !activePlaylist || !detail) return;
    const epIds = new Set<string>();
    for (const eps of Object.values(detail.episodes || {})) {
      if (Array.isArray(eps)) {
        for (const ep of eps) epIds.add(String(ep.id));
      }
    }
    setHasSeriesResume(false);
    // Local cache first — every matching episode row goes.
    await db.resume
      .where("playlistId")
      .equals(activePlaylist.id)
      .filter((r) => r.streamKind === "series" && epIds.has(r.streamId))
      .delete();
    try {
      await Resume.removeSeries(activePlaylist.id, seriesId);
    } catch {
      /* best-effort */
    }
  };

  if (!activePlaylist) return <PageLayout><EmptyState title="Pick a playlist" cta={{ to: "/settings", label: "Open settings" }} /></PageLayout>;
  if (error) return <PageLayout><EmptyState title="Series unavailable" hint={error} /></PageLayout>;

  return (
    <PageLayout>
      <div className="px-4 md:px-8 py-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-text-secondary hover:text-text-primary mb-4 text-[13px] font-ui transition-fast"
        >
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex flex-row gap-4 md:gap-6 mb-8">
          <PosterImage
            src={seriesMeta?.cover}
            alt={seriesMeta?.name || "Series"}
            className="w-[104px] flex-shrink-0 md:w-56 aspect-[2/3] rounded-card border border-line overflow-hidden"
            imgClass="w-full h-full object-cover"
            orientation="stack"
          />
          <div className="flex-1">
            <h1 className="font-display text-[26px] sm:text-[36px] md:text-[44px] tracking-[-0.5px] text-text-primary mb-2 leading-tight">
              {seriesMeta?.name || "Series"}
            </h1>
            {seriesMeta?.genre && (
              <p className="text-[12px] font-mono uppercase tracking-[0.04em] text-text-secondary mb-2">
                {seriesMeta.genre}
              </p>
            )}
            {seriesMeta?.plot && (
              <p className="text-[14px] font-ui text-text-secondary leading-relaxed max-w-2xl line-clamp-4 md:line-clamp-none">
                {seriesMeta.plot}
              </p>
            )}
            {hasSeriesResume && (
              <button
                type="button"
                onClick={markSeriesWatched}
                className="mt-3 h-9 px-3 inline-flex items-center gap-1.5 rounded-pill bg-surface/80 border border-muted/40 hover:border-accent-gold/60 transition-fast text-[12px] font-ui font-medium uppercase tracking-[0.04em] text-text-secondary hover:text-text-primary"
                title="Mark every episode as watched — clears Continue Watching for this series"
              >
                <CheckCircle2 size={15} className="text-accent-gold" />
                Mark series as watched
              </button>
            )}
          </div>
        </div>

        {!detail ? (
          <p className="text-[14px] font-ui text-text-secondary">Loading episodes…</p>
        ) : Object.keys(detail.episodes || {}).length === 0 ? (
          <p className="text-[14px] font-ui text-text-secondary">No episodes found.</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.keys(detail.episodes).map((s) => (
                <button
                  key={s}
                  onClick={() => setActiveSeason(s)}
                  className={`px-3 py-1.5 rounded-pill text-[12px] font-ui font-medium uppercase tracking-[0.04em] border transition-fast ${
                    activeSeason === s
                      ? "bg-accent-gold text-canvas border-accent-gold"
                      : "border-muted/40 text-text-secondary hover:text-text-primary"
                  }`}
                >
                  Season {s}
                </button>
              ))}
            </div>
            <div className="space-y-2">
              {(detail.episodes[activeSeason] || []).map((ep: Episode) => (
                <Link
                  key={ep.id}
                  to={`/watch/episode/${encodeURIComponent(ep.id)}?series=${encodeURIComponent(seriesId)}&season=${encodeURIComponent(activeSeason)}`}
                  state={{
                    episode: ep,
                    seriesTmdbId: (detail.info as Record<string, unknown>)?.tmdb_id,
                    seriesName: (detail.info as Record<string, unknown>)?.name as string | undefined,
                  }}
                  className="block bg-surface hover:bg-card-hover transition-fast rounded-xl p-3 border border-muted/30"
                >
                  <div className="flex gap-3">
                    <PosterImage
                      src={ep.still}
                      alt={ep.title}
                      className="w-32 h-18 sm:w-40 sm:h-24 rounded-lg flex-shrink-0 overflow-hidden"
                      imgClass="w-full h-full object-cover"
                      orientation="row"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-ui font-medium text-text-primary">
                        E{ep.episode_num} · {ep.title}
                      </p>
                      {ep.plot && (
                        <p className="text-[12px] font-ui text-text-secondary line-clamp-2 mt-1">
                          {ep.plot}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
}
